//@ts-nocheck
import React, { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check } from "lucide-react";

// ─── Form-only localization ─────────────────────────────────────────────────
// Japanese is shown only when the user's stored preference is "ja"; every
// other selection (including unset) keeps the form in English. This does not
// touch AI response language or any other UI — see
// AuthContext.applyPreferredLanguage.
const useIsJapanese = () => {
  const [isJapanese, setIsJapanese] = useState(false);
  useEffect(() => {
    try {
      setIsJapanese(localStorage.getItem("preferredLanguage") === "ja");
    } catch {
      setIsJapanese(false);
    }
  }, []);
  return isJapanese;
};

// ─── Desktop detection ────────────────────────────────────────────────────────
const useIsDesktop = (breakpoint = 768) => {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" ? window.innerWidth >= breakpoint : false,
  );
  useEffect(() => {
    const handler = () => setIsDesktop(window.innerWidth >= breakpoint);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);
  return isDesktop;
};

// ─── Types ───────────────────────────────────────────────────────────────────

interface Japan3BoxData {
  blood_type: string | null;
  dob: string | null;
  destiny_time: string | null;
  birth_time: string | null;
  birth_city: string | null;
}

interface Japan3BoxProps {
  onSubmit: (selfData: Japan3BoxData, partnerData: Japan3BoxData) => void;
  isLoading?: boolean;
  categoryName?: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const JP_FONT = "'Noto Sans JP', sans-serif";

const COLORS = {
  // Dark theme (mobile)
  bg: "linear-gradient(160deg, #1a1025 0%, #0d0818 100%)",
  card: "rgba(255,255,255,0.04)",
  cardBorder: "rgba(255,255,255,0.09)",
  cardBorderGold: "rgba(212,175,55,0.55)",
  gold: "#D4AF37",
  goldSoft: "rgba(212,175,55,0.18)",
  white: "#fff",
  whiteDim: "rgba(255,255,255,0.65)",
  whiteVeryDim: "rgba(255,255,255,0.38)",
  inputBg: "rgba(255,255,255,0.06)",
  inputBorder: "rgba(255,255,255,0.12)",
  accent: "rgba(167,139,250,0.55)",
  btnGrad: "linear-gradient(135deg, #c4b5fd 0%, #7c3aed 100%)",
  btnGradGold: "linear-gradient(135deg, #D4AF37 0%, #9e7c1a 100%)",
  resultBg: "rgba(212,175,55,0.06)",
};

// Desktop light-theme colors (EnergyMatchDesign style)
const LIGHT = {
  bg: "linear-gradient(160deg, #F8F4FF 0%, #EDE8F5 100%)",
  card: "#fff",
  cardBorder: "#E5E7EB",
  cardBorderActive: "#C084FC",
  amethyst: "#6D4C92",
  amethystLight: "#C084FC",
  amethystBg: "rgba(192,132,252,0.08)",
  amethystSoft: "rgba(192,132,252,0.15)",
  gold: "#D4AF37",
  goldSoft: "rgba(212,175,55,0.12)",
  textDark: "#5D544D",
  textMuted: "#9CA3AF",
  textLight: "#6A548A",
  white: "#fff",
  inputBg: "#fff",
  inputBorder: "#E5E7EB",
  shadow: "0 2px 12px rgba(109,76,146,0.08)",
  shadowLg: "0 4px 24px rgba(109,76,146,0.14)",
};

const BLOOD_TYPES = ["A", "B", "O", "AB"];

const buildMonths = (isJapanese: boolean) =>
  Array.from({ length: 12 }, (_, i) => ({
    value: String(i + 1).padStart(2, "0"),
    label: isJapanese ? `${i + 1}月` : String(i + 1),
  }));

const buildDays = (isJapanese: boolean) =>
  Array.from({ length: 31 }, (_, i) => ({
    value: String(i + 1).padStart(2, "0"),
    label: isJapanese ? `${i + 1}日` : String(i + 1),
  }));

const buildYears = (isJapanese: boolean) =>
  Array.from({ length: 100 }, (_, i) => {
    const y = new Date().getFullYear() - i;
    return { value: String(y), label: isJapanese ? `${y}年` : String(y) };
  });

const buildHours = (isJapanese: boolean) =>
  Array.from({ length: 24 }, (_, i) => ({
    value: String(i).padStart(2, "0"),
    label: isJapanese ? `${i}時` : String(i).padStart(2, "0"),
  }));

const buildMinutes5 = (isJapanese: boolean) =>
  Array.from({ length: 12 }, (_, i) => ({
    value: String(i * 5).padStart(2, "0"),
    label: isJapanese
      ? `${String(i * 5).padStart(2, "0")}分`
      : String(i * 5).padStart(2, "0"),
  }));

// ─── Slide-Up Panel ──────────────────────────────────────────────────────────

interface SlideUpPanelProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

const SlideUpPanel: React.FC<SlideUpPanelProps> = ({
  isOpen,
  onClose,
  title,
  children,
}) => (
  <AnimatePresence>
    {isOpen && (
      <>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            backdropFilter: "blur(4px)",
            zIndex: 9998,
          }}
        />
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{
            type: "spring",
            damping: 30,
            stiffness: 320,
            duration: 0.25,
          }}
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            maxHeight: "70vh",
            background: "linear-gradient(180deg, #1e1232 0%, #130c22 100%)",
            borderRadius: "20px 20px 0 0",
            padding: "24px 20px 32px",
            zIndex: 9999,
            overflowY: "auto",
            scrollbarWidth: "none",
            fontFamily: JP_FONT,
          }}
        >
          {/* Handle */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: 16,
            }}
          >
            <div
              style={{
                width: 36,
                height: 4,
                borderRadius: 2,
                background: "rgba(255,255,255,0.18)",
              }}
            />
          </div>
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 20,
            }}
          >
            <p
              style={{
                fontSize: 16,
                fontWeight: 500,
                color: COLORS.white,
                fontFamily: JP_FONT,
                margin: 0,
              }}
            >
              {title}
            </p>
            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: COLORS.whiteDim,
                padding: 4,
              }}
            >
              <X size={18} />
            </button>
          </div>
          {children}
        </motion.div>
      </>
    )}
  </AnimatePresence>
);

// ─── Destiny Box Card ─────────────────────────────────────────────────────────

interface DestinyBoxProps {
  icon: string;
  labelJP: string;
  labelEN: string;
  isCompleted: boolean;
  onClick: () => void;
}

const DestinyBox: React.FC<DestinyBoxProps> = ({
  icon,
  labelJP,
  labelEN,
  isCompleted,
  onClick,
}) => (
  <motion.button
    onClick={onClick}
    whileHover={{ scale: 1.03 }}
    whileTap={{ scale: 0.97 }}
    style={{
      flex: 1,
      minWidth: 0,
      height: 110,
      borderRadius: 14,
      background: isCompleted ? COLORS.goldSoft : COLORS.card,
      border: `1px solid ${isCompleted ? COLORS.cardBorderGold : COLORS.cardBorder}`,
      cursor: "pointer",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      padding: "10px 8px",
      position: "relative",
      transition: "border-color 0.2s, background 0.2s",
      boxShadow: isCompleted ? `0 0 14px rgba(212,175,55,0.12)` : "none",
    }}
  >
    {isCompleted && (
      <div
        style={{
          position: "absolute",
          top: 7,
          right: 7,
          width: 18,
          height: 18,
          borderRadius: "50%",
          background: COLORS.gold,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Check size={11} color="#fff" strokeWidth={3} />
      </div>
    )}
    <span style={{ fontSize: 26 }}>{icon}</span>
    <p
      style={{
        fontSize: 11,
        color: isCompleted ? COLORS.gold : COLORS.whiteDim,
        fontFamily: JP_FONT,
        margin: 0,
        textAlign: "center",
        lineHeight: 1.35,
      }}
    >
      {labelJP}
    </p>
    <p
      style={{
        fontSize: 10,
        color: COLORS.whiteVeryDim,
        fontFamily: JP_FONT,
        margin: 0,
      }}
    >
      {labelEN}
    </p>
  </motion.button>
);

// ─── Select Row ───────────────────────────────────────────────────────────────

const SelectRow: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}> = ({ label, value, onChange, options }) => (
  <div style={{ marginBottom: 14 }}>
    <p
      style={{
        fontSize: 13,
        color: COLORS.whiteDim,
        fontFamily: JP_FONT,
        marginBottom: 6,
      }}
    >
      {label}
    </p>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: "100%",
        height: 44,
        borderRadius: 10,
        border: `1px solid ${COLORS.inputBorder}`,
        background: COLORS.inputBg,
        color: value ? COLORS.white : COLORS.whiteVeryDim,
        padding: "0 12px",
        fontSize: 15,
        fontFamily: JP_FONT,
        outline: "none",
        appearance: "none",
        WebkitAppearance: "none",
        cursor: "pointer",
      }}
    >
      <option
        value=""
        style={{ background: "#1a1025", color: COLORS.whiteDim }}
      >
        —
      </option>
      {options.map((o) => (
        <option key={o.value} value={o.value} style={{ background: "#1a1025" }}>
          {o.label}
        </option>
      ))}
    </select>
  </div>
);

// ─── Section Title ────────────────────────────────────────────────────────────

const SectionTitle: React.FC<{ jp: string; en: string }> = ({ jp, en }) => (
  <div style={{ marginBottom: 16 }}>
    <p
      style={{
        fontSize: 15,
        fontWeight: 500,
        color: COLORS.white,
        fontFamily: JP_FONT,
        margin: 0,
      }}
    >
      {jp}
    </p>
    <p
      style={{
        fontSize: 12,
        color: COLORS.whiteVeryDim,
        fontFamily: JP_FONT,
        margin: "2px 0 0",
      }}
    >
      {en}
    </p>
  </div>
);

// ─── Result Section Card ──────────────────────────────────────────────────────

const ResultSectionCard: React.FC<{
  titleJP: string;
  titleEN: string;
  text: string;
  icon: string;
}> = ({ titleJP, titleEN, text, icon }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.35 }}
    style={{
      borderRadius: 14,
      background: COLORS.resultBg,
      border: `1px solid ${COLORS.cardBorderGold}`,
      padding: "16px 18px",
      marginBottom: 12,
      fontFamily: JP_FONT,
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginBottom: 10,
      }}
    >
      <span style={{ fontSize: 18 }}>{icon}</span>
      <div>
        <p
          style={{
            fontSize: 14,
            fontWeight: 500,
            color: COLORS.gold,
            margin: 0,
          }}
        >
          {titleJP}
        </p>
        <p
          style={{
            fontSize: 11,
            color: COLORS.whiteVeryDim,
            margin: "1px 0 0",
          }}
        >
          {titleEN}
        </p>
      </div>
    </div>
    <p
      style={{
        fontSize: 14,
        color: COLORS.whiteDim,
        lineHeight: 1.75,
        margin: 0,
      }}
    >
      {text}
    </p>
  </motion.div>
);

// ─── 3-Box Input Block ────────────────────────────────────────────────────────

interface ThreeBoxBlockProps {
  data: Japan3BoxData;
  onUpdate: (updates: Partial<Japan3BoxData>) => void;
  isPartner?: boolean;
}

const ThreeBoxBlock: React.FC<ThreeBoxBlockProps> = ({
  data,
  onUpdate,
  isPartner = false,
}) => {
  const isDesktop = useIsDesktop();
  const isJapanese = useIsJapanese();
  const MONTHS = buildMonths(isJapanese);
  const DAYS = buildDays(isJapanese);
  const YEARS = buildYears(isJapanese);
  const HOURS = buildHours(isJapanese);
  const MINUTES_5 = buildMinutes5(isJapanese);
  const [openPanel, setOpenPanel] = useState<"blood" | "dob" | "time" | null>(
    null,
  );

  // Local picker state for DOB (used in mobile slide-up panels)
  const [dobY, setDobY] = useState("");
  const [dobM, setDobM] = useState("");
  const [dobD, setDobD] = useState("");

  // Local picker state for Time (used in mobile slide-up panels)
  const [timeH, setTimeH] = useState("");
  const [timeMin, setTimeMin] = useState("");

  const confirmDOB = useCallback(() => {
    if (dobY && dobM && dobD) {
      const formatted = `${dobD}/${dobM}/${dobY}`;
      onUpdate({ dob: formatted });
      setOpenPanel(null);
    }
  }, [dobY, dobM, dobD, onUpdate]);

  const confirmTime = useCallback(() => {
    if (timeH) {
      const formatted = `${timeH}:${timeMin || "00"}`;
      onUpdate({ destiny_time: formatted });
      setOpenPanel(null);
    }
  }, [timeH, timeMin, onUpdate]);

  const prefix = isPartner ? "相手の" : "";

  // ── DESKTOP: White background, inline selects like EnergyMatchDesign ──────────
  if (isDesktop) {
    return (
      <>
        {/* Section header */}
        <div
          style={{
            marginBottom: 16,
            borderBottom: "1px solid #EEF2FF",
            paddingBottom: 10,
          }}
        >
          <p
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: LIGHT.textLight,
              margin: 0,
              fontFamily: JP_FONT,
            }}
          >
            {isPartner ? "相手の「3つの鍵」" : "あなたの「3つの鍵」"}
          </p>
          <p
            style={{
              fontSize: 11,
              color: LIGHT.textMuted,
              margin: "2px 0 0",
              fontFamily: JP_FONT,
            }}
          >
            {isPartner
              ? "Three Keys of Your Partner"
              : "Three Keys of Your Destiny"}
          </p>
        </div>

        {/* 3 Columns — Blood Type, DOB, Destiny Time */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 14,
            marginBottom: 16,
          }}
        >
          {/* Blood Type */}
          <div
            style={{
              background: LIGHT.card,
              border: `1px solid ${data.blood_type ? LIGHT.cardBorderActive : LIGHT.cardBorder}`,
              borderRadius: 14,
              padding: "14px 12px",
              position: "relative",
              boxShadow: LIGHT.shadow,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 10,
              }}
            >
              <div>
                <p
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: LIGHT.textLight,
                    margin: 0,
                    fontFamily: JP_FONT,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                  }}
                >
                  {prefix}血液型
                </p>
                <p
                  style={{
                    fontSize: 10,
                    color: LIGHT.textMuted,
                    margin: "2px 0 0",
                  }}
                >
                  Blood Type
                </p>
              </div>
              <span style={{ fontSize: 18 }}>🩸</span>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              {BLOOD_TYPES.map((bt) => (
                <button
                  key={bt}
                  onClick={() => onUpdate({ blood_type: bt })}
                  style={{
                    flex: 1,
                    height: 38,
                    borderRadius: 10,
                    background:
                      data.blood_type === bt
                        ? LIGHT.amethystSoft
                        : LIGHT.inputBg,
                    border: `1.5px solid ${data.blood_type === bt ? LIGHT.cardBorderActive : LIGHT.cardBorder}`,
                    color:
                      data.blood_type === bt ? LIGHT.amethyst : LIGHT.textDark,
                    fontSize: 13,
                    fontWeight: 700,
                    fontFamily: JP_FONT,
                    cursor: "pointer",
                    transition: "all 0.18s",
                    boxShadow:
                      data.blood_type === bt
                        ? `0 0 0 3px ${LIGHT.amethystSoft}`
                        : "none",
                  }}
                >
                  {bt}
                </button>
              ))}
            </div>
            {data.blood_type && (
              <div
                style={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  background: LIGHT.amethyst,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Check size={9} color="#fff" strokeWidth={3} />
              </div>
            )}
          </div>

          {/* Date of Birth */}
          <div
            style={{
              background: LIGHT.card,
              border: `1px solid ${data.dob ? LIGHT.cardBorderActive : LIGHT.cardBorder}`,
              borderRadius: 14,
              padding: "14px 12px",
              position: "relative",
              boxShadow: LIGHT.shadow,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 10,
              }}
            >
              <div>
                <p
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: LIGHT.textLight,
                    margin: 0,
                    fontFamily: JP_FONT,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                  }}
                >
                  {prefix}生年月日
                </p>
                <p
                  style={{
                    fontSize: 10,
                    color: LIGHT.textMuted,
                    margin: "2px 0 0",
                  }}
                >
                  Date of Birth
                </p>
              </div>
              <span style={{ fontSize: 18 }}>📅</span>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 5,
              }}
            >
              <select
                value={dobY || ""}
                onChange={(e) => {
                  setDobY(e.target.value);
                  if (e.target.value && dobM && dobD) {
                    onUpdate({ dob: `${dobD}/${dobM}/${e.target.value}` });
                  }
                }}
                style={{
                  height: 36,
                  borderRadius: 8,
                  border: `1px solid ${LIGHT.inputBorder}`,
                  background: LIGHT.inputBg,
                  color: dobY ? LIGHT.textDark : LIGHT.textMuted,
                  padding: "0 6px",
                  fontSize: 12,
                  fontFamily: JP_FONT,
                  outline: "none",
                  cursor: "pointer",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <option value="">年</option>
                {YEARS.map((y) => (
                  <option key={y.value} value={y.value}>
                    {y.label}
                  </option>
                ))}
              </select>
              <select
                value={dobM || ""}
                onChange={(e) => {
                  setDobM(e.target.value);
                  if (dobY && e.target.value && dobD) {
                    onUpdate({ dob: `${dobD}/${e.target.value}/${dobY}` });
                  }
                }}
                style={{
                  height: 36,
                  borderRadius: 8,
                  border: `1px solid ${LIGHT.inputBorder}`,
                  background: LIGHT.inputBg,
                  color: dobM ? LIGHT.textDark : LIGHT.textMuted,
                  padding: "0 6px",
                  fontSize: 12,
                  fontFamily: JP_FONT,
                  outline: "none",
                  cursor: "pointer",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <option value="">{t.monthOptionShort}</option>
                {MONTHS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
              <select
                value={dobD || ""}
                onChange={(e) => {
                  setDobD(e.target.value);
                  if (dobY && dobM && e.target.value) {
                    onUpdate({ dob: `${e.target.value}/${dobM}/${dobY}` });
                  }
                }}
                style={{
                  height: 36,
                  borderRadius: 8,
                  border: `1px solid ${LIGHT.inputBorder}`,
                  background: LIGHT.inputBg,
                  color: dobD ? LIGHT.textDark : LIGHT.textMuted,
                  padding: "0 6px",
                  fontSize: 12,
                  fontFamily: JP_FONT,
                  outline: "none",
                  cursor: "pointer",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <option value="">{t.dayOptionShort}</option>
                {DAYS.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
            </div>
            {data.dob && (
              <div
                style={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  background: LIGHT.amethyst,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Check size={9} color="#fff" strokeWidth={3} />
              </div>
            )}
          </div>

          {/* Destiny Time */}
          <div
            style={{
              background: LIGHT.card,
              border: `1px solid ${data.destiny_time ? LIGHT.cardBorderActive : LIGHT.cardBorder}`,
              borderRadius: 14,
              padding: "14px 12px",
              position: "relative",
              boxShadow: LIGHT.shadow,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 10,
              }}
            >
              <div>
                <p
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: LIGHT.textLight,
                    margin: 0,
                    fontFamily: JP_FONT,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                  }}
                >
                  {prefix}生まれた時間
                </p>
                <p
                  style={{
                    fontSize: 10,
                    color: LIGHT.textMuted,
                    margin: "2px 0 0",
                  }}
                >
                  Destiny Time
                </p>
              </div>
              <span style={{ fontSize: 18 }}>🕰️</span>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 5,
              }}
            >
              <select
                value={timeH || ""}
                onChange={(e) => {
                  setTimeH(e.target.value);
                  if (e.target.value)
                    onUpdate({
                      destiny_time: `${e.target.value}:${timeMin || "00"}`,
                    });
                }}
                style={{
                  height: 36,
                  borderRadius: 8,
                  border: `1px solid ${LIGHT.inputBorder}`,
                  background: LIGHT.inputBg,
                  color: timeH ? LIGHT.textDark : LIGHT.textMuted,
                  padding: "0 6px",
                  fontSize: 12,
                  fontFamily: JP_FONT,
                  outline: "none",
                  cursor: "pointer",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <option value="">{t.hourOptionShort}</option>
                {HOURS.map((h) => (
                  <option key={h.value} value={h.value}>
                    {h.label}
                  </option>
                ))}
              </select>
              <select
                value={timeMin || ""}
                onChange={(e) => {
                  setTimeMin(e.target.value);
                  if (timeH)
                    onUpdate({ destiny_time: `${timeH}:${e.target.value}` });
                }}
                style={{
                  height: 36,
                  borderRadius: 8,
                  border: `1px solid ${LIGHT.inputBorder}`,
                  background: LIGHT.inputBg,
                  color: timeMin ? LIGHT.textDark : LIGHT.textMuted,
                  padding: "0 6px",
                  fontSize: 12,
                  fontFamily: JP_FONT,
                  outline: "none",
                  cursor: "pointer",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <option value="">{t.minuteOptionShort}</option>
                {MINUTES_5.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
            {data.destiny_time && (
              <div
                style={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  background: LIGHT.amethyst,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Check size={9} color="#fff" strokeWidth={3} />
              </div>
            )}
          </div>
        </div>

        {/* Birth City */}
        <div style={{ marginBottom: 4 }}>
          <p
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: LIGHT.textLight,
              fontFamily: JP_FONT,
              marginBottom: 6,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
          >
            {isPartner ? "相手の出生地" : "出生地"}{" "}
            <span
              style={{
                fontWeight: 400,
                color: LIGHT.textMuted,
                textTransform: "none",
                letterSpacing: 0,
              }}
            >
              (任意 / Optional)
            </span>
          </p>
          <input
            type="text"
            value={data.birth_city || ""}
            onChange={(e) => onUpdate({ birth_city: e.target.value })}
            placeholder={isPartner ? "都市 / 地域" : "都道府県 / 市区町村"}
            style={{
              width: "100%",
              height: 40,
              borderRadius: 10,
              border: `1px solid ${LIGHT.inputBorder}`,
              background: LIGHT.inputBg,
              color: LIGHT.textDark,
              padding: "0 12px",
              fontSize: 14,
              fontFamily: JP_FONT,
              outline: "none",
              boxSizing: "border-box",
            }}
          />
        </div>
      </>
    );
  }

  // ── MOBILE: Original slide-up panel experience ────────────────────────────
  return (
    <>
      {/* 3 Boxes */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
        <DestinyBox
          icon="🩸"
          labelJP={`${prefix}血液型`}
          labelEN="Blood Type"
          isCompleted={!!data.blood_type}
          onClick={() => setOpenPanel("blood")}
        />
        <DestinyBox
          icon="📅"
          labelJP={`${prefix}生年月日`}
          labelEN="Date of Birth"
          isCompleted={!!data.dob}
          onClick={() => setOpenPanel("dob")}
        />
        <DestinyBox
          icon="🕰️"
          labelJP={`${prefix}生まれた時間`}
          labelEN="Destiny Time"
          isCompleted={!!data.destiny_time}
          onClick={() => setOpenPanel("time")}
        />
      </div>

      {/* Birth City (text input, inline) */}
      <div style={{ marginBottom: 4 }}>
        <p
          style={{
            fontSize: 12,
            color: COLORS.whiteVeryDim,
            fontFamily: JP_FONT,
            marginBottom: 6,
          }}
        >
          {isPartner ? "相手の出生地（任意）" : "出生地（任意）"}
        </p>
        <input
          type="text"
          value={data.birth_city || ""}
          onChange={(e) => onUpdate({ birth_city: e.target.value })}
          placeholder={isPartner ? "都市 / 地域" : "都道府県 / 市区町村"}
          style={{
            width: "100%",
            height: 40,
            borderRadius: 10,
            border: `1px solid ${COLORS.inputBorder}`,
            background: COLORS.inputBg,
            color: COLORS.white,
            padding: "0 12px",
            fontSize: 14,
            fontFamily: JP_FONT,
            outline: "none",
            boxSizing: "border-box",
          }}
        />
      </div>

      {/* Blood Type Panel */}
      <SlideUpPanel
        isOpen={openPanel === "blood"}
        onClose={() => setOpenPanel(null)}
        title={
          isPartner ? "相手の血液型を選んでください" : "血液型を選んでください"
        }
      >
        <div
          style={{
            display: "flex",
            gap: 10,
            justifyContent: "center",
            flexWrap: "wrap",
            marginBottom: 20,
          }}
        >
          {BLOOD_TYPES.map((bt) => (
            <motion.button
              key={bt}
              whileTap={{ scale: 0.94 }}
              onClick={() => {
                onUpdate({ blood_type: bt });
                setOpenPanel(null);
              }}
              style={{
                width: 72,
                height: 72,
                borderRadius: 14,
                background:
                  data.blood_type === bt ? COLORS.goldSoft : COLORS.inputBg,
                border: `2px solid ${data.blood_type === bt ? COLORS.gold : COLORS.inputBorder}`,
                color: data.blood_type === bt ? COLORS.gold : COLORS.white,
                fontSize: 20,
                fontWeight: 600,
                fontFamily: JP_FONT,
                cursor: "pointer",
                transition: "all 0.18s",
              }}
            >
              {bt}
            </motion.button>
          ))}
        </div>
        <p
          style={{
            fontSize: 12,
            color: COLORS.whiteVeryDim,
            fontFamily: JP_FONT,
            textAlign: "center",
          }}
        >
          {isPartner
            ? "相手の血液型を選ぶと、雰囲気の相性が読み解かれます。"
            : "血液型を選ぶと、あなたの雰囲気が読み解かれます。"}
        </p>
      </SlideUpPanel>

      {/* DOB Panel */}
      <SlideUpPanel
        isOpen={openPanel === "dob"}
        onClose={() => setOpenPanel(null)}
        title={
          isPartner
            ? "相手の生年月日を選んでください"
            : "生年月日を選んでください"
        }
      >
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <div style={{ flex: 2 }}>
            <SelectRow
              label="年"
              value={dobY}
              onChange={setDobY}
              options={YEARS}
            />
          </div>
          <div style={{ flex: 1 }}>
            <SelectRow
              label="月"
              value={dobM}
              onChange={setDobM}
              options={MONTHS}
            />
          </div>
          <div style={{ flex: 1 }}>
            <SelectRow
              label="日"
              value={dobD}
              onChange={setDobD}
              options={DAYS}
            />
          </div>
        </div>
        <motion.button
          whileTap={{ scale: 0.97 }}
          onClick={confirmDOB}
          disabled={!dobY || !dobM || !dobD}
          style={{
            width: "100%",
            height: 50,
            borderRadius: 14,
            background:
              dobY && dobM && dobD
                ? COLORS.btnGradGold
                : "rgba(255,255,255,0.07)",
            color: dobY && dobM && dobD ? "#fff" : COLORS.whiteVeryDim,
            fontSize: 15,
            fontFamily: JP_FONT,
            border: "none",
            cursor: dobY && dobM && dobD ? "pointer" : "not-allowed",
            transition: "all 0.2s",
          }}
        >
          {isPartner ? "相手の空気感を見る" : "空気感を見る"}
        </motion.button>
      </SlideUpPanel>

      {/* Destiny Time Panel */}
      <SlideUpPanel
        isOpen={openPanel === "time"}
        onClose={() => setOpenPanel(null)}
        title={
          isPartner
            ? "相手の生まれた時間を選んでください"
            : "生まれた時間を選んでください"
        }
      >
        <div
          style={{
            display: "flex",
            gap: 12,
            marginBottom: 16,
            alignItems: "flex-end",
          }}
        >
          <div style={{ flex: 1 }}>
            <SelectRow
              label="時"
              value={timeH}
              onChange={setTimeH}
              options={HOURS}
            />
          </div>
          <div style={{ flex: 1 }}>
            <SelectRow
              label="分"
              value={timeMin}
              onChange={setTimeMin}
              options={MINUTES_5}
            />
          </div>
        </div>
        <motion.button
          whileTap={{ scale: 0.97 }}
          onClick={confirmTime}
          disabled={!timeH}
          style={{
            width: "100%",
            height: 50,
            borderRadius: 14,
            background: timeH ? COLORS.btnGradGold : "rgba(255,255,255,0.07)",
            color: timeH ? "#fff" : COLORS.whiteVeryDim,
            fontSize: 15,
            fontFamily: JP_FONT,
            border: "none",
            cursor: timeH ? "pointer" : "not-allowed",
            transition: "all 0.2s",
          }}
        >
          {isPartner ? "流れを見る" : "流れを見る"}
        </motion.button>
      </SlideUpPanel>
    </>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const EMPTY_DATA = (): Japan3BoxData => ({
  blood_type: null,
  dob: null,
  destiny_time: null,
  birth_time: null,
  birth_city: null,
});

type Step = "self" | "partner" | "result";

// ─── Localized copy ───────────────────────────────────────────────────────────

const buildCopy = (isJapanese: boolean) =>
  isJapanese
    ? {
        pageTitle: "💞 Japan Compatibility",
        pageSubtitle: "二人の生年月日を入力して、相性を占いましょう",
        personAHeading: "Person A — あなたの情報",
        personBHeading: "Person B — 相手の情報",
        bloodTypeLabel: "Blood Type 血液型",
        dobLabel: "Date of Birth 生年月日",
        destinyTimeLabel: "Destiny Time 生まれた時間",
        optional: "(任意)",
        placeOfBirthLabel: "Place of Birth 出生地",
        selfCityPlaceholder: "都道府県 / 市区町村",
        partnerCityPlaceholder: "都市 / 地域",
        yearOption: "年 / Year",
        monthOption: "月 / Month",
        dayOption: "日 / Day",
        monthOptionShort: "月",
        dayOptionShort: "日",
        hourOption: "時 / Hour",
        minuteOption: "分 / Min",
        hourOptionShort: "時",
        minuteOptionShort: "分",
        submitButton: "✨ Check Japan Compatibility",
        loadingButton: "読み解いています...",
      }
    : {
        pageTitle: "💞 Japan Compatibility",
        pageSubtitle:
          "Enter birth details for both people to analyze compatibility",
        personAHeading: "Person A — Your Info",
        personBHeading: "Person B — Partner Info",
        bloodTypeLabel: "Blood Type",
        dobLabel: "Date of Birth",
        destinyTimeLabel: "Destiny Time",
        optional: "(optional)",
        placeOfBirthLabel: "Place of Birth",
        selfCityPlaceholder: "Prefecture / City",
        partnerCityPlaceholder: "City / Region",
        yearOption: "Year",
        monthOption: "Month",
        dayOption: "Day",
        monthOptionShort: "Month",
        dayOptionShort: "Day",
        hourOption: "Hour",
        minuteOption: "Min",
        hourOptionShort: "Hour",
        minuteOptionShort: "Min",
        submitButton: "✨ Check Japan Compatibility",
        loadingButton: "Reading your compatibility...",
      };

const JapanCompatibility3Box: React.FC<Japan3BoxProps> = ({
  onSubmit,
  isLoading = false,
}) => {
  const isDesktop = useIsDesktop();
  const isJapanese = useIsJapanese();
  const t = buildCopy(isJapanese);
  const MONTHS = buildMonths(isJapanese);
  const DAYS = buildDays(isJapanese);
  const YEARS = buildYears(isJapanese);
  const HOURS = buildHours(isJapanese);
  const MINUTES_5 = buildMinutes5(isJapanese);
  const [step, setStep] = useState<Step>("self");
  const [selfData, setSelfData] = useState<Japan3BoxData>(EMPTY_DATA());
  const [partnerData, setPartnerData] = useState<Japan3BoxData>(EMPTY_DATA());

  const isSelfComplete = !!(
    selfData.blood_type ||
    selfData.dob ||
    selfData.destiny_time
  );
  const isPartnerComplete = !!(
    partnerData.blood_type ||
    partnerData.dob ||
    partnerData.destiny_time
  );
  const bothComplete = isSelfComplete && isPartnerComplete;

  const updateSelf = useCallback((updates: Partial<Japan3BoxData>) => {
    setSelfData((prev) => ({ ...prev, ...updates }));
  }, []);

  const updatePartner = useCallback((updates: Partial<Japan3BoxData>) => {
    setPartnerData((prev) => ({ ...prev, ...updates }));
  }, []);

  // Desktop: blood_type and dob are required; destiny_time is optional (任意)
  const selfAllFilled = !!(selfData.blood_type && selfData.dob);
  const partnerAllFilled = !!(partnerData.blood_type && partnerData.dob);
  const bothAllFilled = selfAllFilled && partnerAllFilled;

  const handleCalculate = useCallback(() => {
    console.log("🔥 handleCalculate called", {
      isSelfComplete,
      isPartnerComplete,
      isLoading,
      selfData: JSON.stringify(selfData),
      partnerData: JSON.stringify(partnerData),
    });
    if (!isSelfComplete || !isPartnerComplete || isLoading) {
      console.log("🔥 handleCalculate BLOCKED", {
        isSelfComplete,
        isPartnerComplete,
        isLoading,
      });
      return;
    }
    console.log("🔥 handleCalculate proceeding, calling onSubmit...");
    setStep("result");
    onSubmit(selfData, partnerData);
    console.log("🔥 handleCalculate onSubmit called");
  }, [
    isSelfComplete,
    isPartnerComplete,
    isLoading,
    selfData,
    partnerData,
    onSubmit,
  ]);

  // ── DESKTOP LAYOUT — EnergyMatchDesign style, single form, 2 columns ─────────
  if (isDesktop) {
    return (
      <div
        className="w-full px-4 sm:px-6 md:px-10 lg:px-16 xl:px-20 py-8 sm:py-10 lg:py-12"
        style={{
          fontFamily: JP_FONT,
        }}
      >
        <div className="w-full max-w-5xl mx-auto">
          {/* Page title */}
          <div style={{ textAlign: "center", marginBottom: 28 }}>
            <p
              style={{
                fontSize: 22,
                fontWeight: 600,
                color: LIGHT.textLight,
                margin: "0 0 4px",
                fontFamily: JP_FONT,
              }}
            >
              {t.pageTitle}
            </p>
            <p
              style={{
                fontSize: 13,
                color: LIGHT.textMuted,
                margin: 0,
                fontFamily: JP_FONT,
              }}
            >
              {t.pageSubtitle}
            </p>
          </div>

          {/* 2-column form grid — EnergyMatchDesign style */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "32px 48px",
              marginBottom: 32,
            }}
          >
            {/* ── LEFT: Person A (You) ── */}
            <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {/* Person header */}
              <div
                style={{
                  borderBottom: "2px solid #EEF2FF",
                  paddingBottom: 8,
                  marginBottom: 18,
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: LIGHT.amethyst,
                    fontFamily: JP_FONT,
                    textTransform: "uppercase",
                    letterSpacing: "0.12em",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      background: LIGHT.amethyst,
                      display: "inlineBlock",
                      flexShrink: 0,
                    }}
                  />
                  {t.personAHeading}
                </span>
              </div>

              {/* Blood Type */}
              <div style={{ marginBottom: 16 }}>
                <label
                  style={{
                    display: "block",
                    fontSize: 11,
                    fontWeight: 600,
                    color: LIGHT.textMuted,
                    fontFamily: JP_FONT,
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    marginBottom: 8,
                  }}
                >
                  {t.bloodTypeLabel}
                </label>
                <div style={{ display: "flex", gap: 8 }}>
                  {BLOOD_TYPES.map((bt) => (
                    <button
                      key={bt}
                      type="button"
                      onClick={() => updateSelf({ blood_type: bt })}
                      style={{
                        flex: 1,
                        height: 44,
                        borderRadius: 12,
                        background:
                          selfData.blood_type === bt
                            ? LIGHT.amethystSoft
                            : LIGHT.card,
                        border: `1.5px solid ${selfData.blood_type === bt ? LIGHT.cardBorderActive : LIGHT.cardBorder}`,
                        color:
                          selfData.blood_type === bt
                            ? LIGHT.amethyst
                            : LIGHT.textDark,
                        fontSize: 14,
                        fontWeight: 700,
                        fontFamily: JP_FONT,
                        cursor: "pointer",
                        transition: "all 0.18s",
                        boxShadow:
                          selfData.blood_type === bt
                            ? `0 0 0 3px ${LIGHT.amethystSoft}`
                            : "none",
                      }}
                    >
                      {bt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Date of Birth */}
              <div style={{ marginBottom: 16 }}>
                <label
                  style={{
                    display: "block",
                    fontSize: 11,
                    fontWeight: 600,
                    color: LIGHT.textMuted,
                    fontFamily: JP_FONT,
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    marginBottom: 8,
                  }}
                >
                  {t.dobLabel}
                </label>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "2fr 1fr 1fr",
                    gap: 8,
                  }}
                >
                  <select
                    value={selfData.dob ? selfData.dob.split("/")[2] || "" : ""}
                    onChange={(e) => {
                      const d = selfData.dob?.split("/") || ["", "", ""];
                      const updated = `${d[0] || ""}/${d[1] || ""}/${e.target.value}`;
                      updateSelf({ dob: e.target.value ? updated : null });
                    }}
                    style={{
                      height: 44,
                      borderRadius: 12,
                      border: `1.5px solid ${LIGHT.cardBorder}`,
                      background: LIGHT.card,
                      color: LIGHT.textDark,
                      padding: "0 12px",
                      fontSize: 13,
                      fontFamily: JP_FONT,
                      outline: "none",
                      cursor: "pointer",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                    }}
                  >
                    <option value="">{t.yearOption}</option>
                    {YEARS.map((y) => (
                      <option key={y.value} value={y.value}>
                        {y.label}
                      </option>
                    ))}
                  </select>
                  <select
                    value={selfData.dob ? selfData.dob.split("/")[1] || "" : ""}
                    onChange={(e) => {
                      const d = selfData.dob?.split("/") || ["", "", ""];
                      const updated = `${d[0] || ""}/${e.target.value}/${d[2] || ""}`;
                      updateSelf({ dob: e.target.value ? updated : null });
                    }}
                    style={{
                      height: 44,
                      borderRadius: 12,
                      border: `1.5px solid ${LIGHT.cardBorder}`,
                      background: LIGHT.card,
                      color: LIGHT.textDark,
                      padding: "0 8px",
                      fontSize: 13,
                      fontFamily: JP_FONT,
                      outline: "none",
                      cursor: "pointer",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                    }}
                  >
                    <option value="">{t.monthOption}</option>
                    {MONTHS.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                  <select
                    value={selfData.dob ? selfData.dob.split("/")[0] || "" : ""}
                    onChange={(e) => {
                      const d = selfData.dob?.split("/") || ["", "", ""];
                      const updated = `${e.target.value}/${d[1] || ""}/${d[2] || ""}`;
                      updateSelf({ dob: e.target.value ? updated : null });
                    }}
                    style={{
                      height: 44,
                      borderRadius: 12,
                      border: `1.5px solid ${LIGHT.cardBorder}`,
                      background: LIGHT.card,
                      color: LIGHT.textDark,
                      padding: "0 8px",
                      fontSize: 13,
                      fontFamily: JP_FONT,
                      outline: "none",
                      cursor: "pointer",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                    }}
                  >
                    <option value="">{t.dayOption}</option>
                    {DAYS.map((d) => (
                      <option key={d.value} value={d.value}>
                        {d.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Destiny Time */}
              <div style={{ marginBottom: 16 }}>
                <label
                  style={{
                    display: "block",
                    fontSize: 11,
                    fontWeight: 600,
                    color: LIGHT.textMuted,
                    fontFamily: JP_FONT,
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    marginBottom: 8,
                  }}
                >
                  {t.destinyTimeLabel}{" "}
                  <span
                    style={{
                      fontWeight: 400,
                      textTransform: "none",
                      color: LIGHT.textMuted,
                    }}
                  >
                    {t.optional}
                  </span>
                </label>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 8,
                  }}
                >
                  <select
                    value={
                      selfData.destiny_time
                        ? selfData.destiny_time.split(":")[0] || ""
                        : ""
                    }
                    onChange={(e) =>
                      updateSelf({
                        destiny_time: e.target.value
                          ? `${e.target.value}:${selfData.destiny_time?.split(":")[1] || "00"}`
                          : null,
                      })
                    }
                    style={{
                      height: 44,
                      borderRadius: 12,
                      border: `1.5px solid ${LIGHT.cardBorder}`,
                      background: LIGHT.card,
                      color: LIGHT.textDark,
                      padding: "0 12px",
                      fontSize: 13,
                      fontFamily: JP_FONT,
                      outline: "none",
                      cursor: "pointer",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                    }}
                  >
                    <option value="">{t.hourOption}</option>
                    {HOURS.map((h) => (
                      <option key={h.value} value={h.value}>
                        {h.label}
                      </option>
                    ))}
                  </select>
                  <select
                    value={
                      selfData.destiny_time
                        ? selfData.destiny_time.split(":")[1] || ""
                        : ""
                    }
                    onChange={(e) =>
                      updateSelf({
                        destiny_time: selfData.destiny_time
                          ? `${selfData.destiny_time.split(":")[0]}:${e.target.value}`
                          : null,
                      })
                    }
                    style={{
                      height: 44,
                      borderRadius: 12,
                      border: `1.5px solid ${LIGHT.cardBorder}`,
                      background: LIGHT.card,
                      color: LIGHT.textDark,
                      padding: "0 12px",
                      fontSize: 13,
                      fontFamily: JP_FONT,
                      outline: "none",
                      cursor: "pointer",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                    }}
                  >
                    <option value="">{t.minuteOption}</option>
                    {MINUTES_5.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Birth City */}
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: 11,
                    fontWeight: 600,
                    color: LIGHT.textMuted,
                    fontFamily: JP_FONT,
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    marginBottom: 8,
                  }}
                >
                  {t.placeOfBirthLabel}{" "}
                  <span
                    style={{
                      fontWeight: 400,
                      textTransform: "none",
                      color: LIGHT.textMuted,
                    }}
                  >
                    {t.optional}
                  </span>
                </label>
                <input
                  type="text"
                  value={selfData.birth_city || ""}
                  onChange={(e) => updateSelf({ birth_city: e.target.value })}
                  placeholder={t.selfCityPlaceholder}
                  style={{
                    width: "100%",
                    height: 44,
                    borderRadius: 12,
                    border: `1.5px solid ${LIGHT.cardBorder}`,
                    background: LIGHT.card,
                    color: LIGHT.textDark,
                    padding: "0 12px",
                    fontSize: 14,
                    fontFamily: JP_FONT,
                    outline: "none",
                    boxSizing: "border-box",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                  }}
                />
              </div>
            </div>

            {/* ── RIGHT: Person B (Partner) ── */}
            <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {/* Person header */}
              <div
                style={{
                  borderBottom: "2px solid #EEF2FF",
                  paddingBottom: 8,
                  marginBottom: 18,
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#7DD3FC",
                    fontFamily: JP_FONT,
                    textTransform: "uppercase",
                    letterSpacing: "0.12em",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      background: "#7DD3FC",
                      display: "inlineBlock",
                      flexShrink: 0,
                    }}
                  />
                  {t.personBHeading}
                </span>
              </div>

              {/* Blood Type */}
              <div style={{ marginBottom: 16 }}>
                <label
                  style={{
                    display: "block",
                    fontSize: 11,
                    fontWeight: 600,
                    color: LIGHT.textMuted,
                    fontFamily: JP_FONT,
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    marginBottom: 8,
                  }}
                >
                  {t.bloodTypeLabel}
                </label>
                <div style={{ display: "flex", gap: 8 }}>
                  {BLOOD_TYPES.map((bt) => (
                    <button
                      key={bt}
                      type="button"
                      onClick={() => updatePartner({ blood_type: bt })}
                      style={{
                        flex: 1,
                        height: 44,
                        borderRadius: 12,
                        background:
                          partnerData.blood_type === bt
                            ? "rgba(125,211,252,0.15)"
                            : LIGHT.card,
                        border: `1.5px solid ${partnerData.blood_type === bt ? "#7DD3FC" : LIGHT.cardBorder}`,
                        color:
                          partnerData.blood_type === bt
                            ? "#7DD3FC"
                            : LIGHT.textDark,
                        fontSize: 14,
                        fontWeight: 700,
                        fontFamily: JP_FONT,
                        cursor: "pointer",
                        transition: "all 0.18s",
                        boxShadow:
                          partnerData.blood_type === bt
                            ? "0 0 0 3px rgba(125,211,252,0.15)"
                            : "none",
                      }}
                    >
                      {bt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Date of Birth */}
              <div style={{ marginBottom: 16 }}>
                <label
                  style={{
                    display: "block",
                    fontSize: 11,
                    fontWeight: 600,
                    color: LIGHT.textMuted,
                    fontFamily: JP_FONT,
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    marginBottom: 8,
                  }}
                >
                  {t.dobLabel}
                </label>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "2fr 1fr 1fr",
                    gap: 8,
                  }}
                >
                  <select
                    value={
                      partnerData.dob ? partnerData.dob.split("/")[2] || "" : ""
                    }
                    onChange={(e) => {
                      const d = partnerData.dob?.split("/") || ["", "", ""];
                      const updated = `${d[0] || ""}/${d[1] || ""}/${e.target.value}`;
                      updatePartner({ dob: e.target.value ? updated : null });
                    }}
                    style={{
                      height: 44,
                      borderRadius: 12,
                      border: `1.5px solid ${LIGHT.cardBorder}`,
                      background: LIGHT.card,
                      color: LIGHT.textDark,
                      padding: "0 12px",
                      fontSize: 13,
                      fontFamily: JP_FONT,
                      outline: "none",
                      cursor: "pointer",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                    }}
                  >
                    <option value="">{t.yearOption}</option>
                    {YEARS.map((y) => (
                      <option key={y.value} value={y.value}>
                        {y.label}
                      </option>
                    ))}
                  </select>
                  <select
                    value={
                      partnerData.dob ? partnerData.dob.split("/")[1] || "" : ""
                    }
                    onChange={(e) => {
                      const d = partnerData.dob?.split("/") || ["", "", ""];
                      const updated = `${d[0] || ""}/${e.target.value}/${d[2] || ""}`;
                      updatePartner({ dob: e.target.value ? updated : null });
                    }}
                    style={{
                      height: 44,
                      borderRadius: 12,
                      border: `1.5px solid ${LIGHT.cardBorder}`,
                      background: LIGHT.card,
                      color: LIGHT.textDark,
                      padding: "0 8px",
                      fontSize: 13,
                      fontFamily: JP_FONT,
                      outline: "none",
                      cursor: "pointer",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                    }}
                  >
                    <option value="">{t.monthOption}</option>
                    {MONTHS.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                  <select
                    value={
                      partnerData.dob ? partnerData.dob.split("/")[0] || "" : ""
                    }
                    onChange={(e) => {
                      const d = partnerData.dob?.split("/") || ["", "", ""];
                      const updated = `${e.target.value}/${d[1] || ""}/${d[2] || ""}`;
                      updatePartner({ dob: e.target.value ? updated : null });
                    }}
                    style={{
                      height: 44,
                      borderRadius: 12,
                      border: `1.5px solid ${LIGHT.cardBorder}`,
                      background: LIGHT.card,
                      color: LIGHT.textDark,
                      padding: "0 8px",
                      fontSize: 13,
                      fontFamily: JP_FONT,
                      outline: "none",
                      cursor: "pointer",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                    }}
                  >
                    <option value="">{t.dayOption}</option>
                    {DAYS.map((d) => (
                      <option key={d.value} value={d.value}>
                        {d.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Destiny Time */}
              <div style={{ marginBottom: 16 }}>
                <label
                  style={{
                    display: "block",
                    fontSize: 11,
                    fontWeight: 600,
                    color: LIGHT.textMuted,
                    fontFamily: JP_FONT,
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    marginBottom: 8,
                  }}
                >
                  {t.destinyTimeLabel}{" "}
                  <span
                    style={{
                      fontWeight: 400,
                      textTransform: "none",
                      color: LIGHT.textMuted,
                    }}
                  >
                    {t.optional}
                  </span>
                </label>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 8,
                  }}
                >
                  <select
                    value={
                      partnerData.destiny_time
                        ? partnerData.destiny_time.split(":")[0] || ""
                        : ""
                    }
                    onChange={(e) =>
                      updatePartner({
                        destiny_time: e.target.value
                          ? `${e.target.value}:${partnerData.destiny_time?.split(":")[1] || "00"}`
                          : null,
                      })
                    }
                    style={{
                      height: 44,
                      borderRadius: 12,
                      border: `1.5px solid ${LIGHT.cardBorder}`,
                      background: LIGHT.card,
                      color: LIGHT.textDark,
                      padding: "0 12px",
                      fontSize: 13,
                      fontFamily: JP_FONT,
                      outline: "none",
                      cursor: "pointer",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                    }}
                  >
                    <option value="">{t.hourOption}</option>
                    {HOURS.map((h) => (
                      <option key={h.value} value={h.value}>
                        {h.label}
                      </option>
                    ))}
                  </select>
                  <select
                    value={
                      partnerData.destiny_time
                        ? partnerData.destiny_time.split(":")[1] || ""
                        : ""
                    }
                    onChange={(e) =>
                      updatePartner({
                        destiny_time: partnerData.destiny_time
                          ? `${partnerData.destiny_time.split(":")[0]}:${e.target.value}`
                          : null,
                      })
                    }
                    style={{
                      height: 44,
                      borderRadius: 12,
                      border: `1.5px solid ${LIGHT.cardBorder}`,
                      background: LIGHT.card,
                      color: LIGHT.textDark,
                      padding: "0 12px",
                      fontSize: 13,
                      fontFamily: JP_FONT,
                      outline: "none",
                      cursor: "pointer",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                    }}
                  >
                    <option value="">{t.minuteOption}</option>
                    {MINUTES_5.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Birth City */}
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: 11,
                    fontWeight: 600,
                    color: LIGHT.textMuted,
                    fontFamily: JP_FONT,
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    marginBottom: 8,
                  }}
                >
                  {t.placeOfBirthLabel}{" "}
                  <span
                    style={{
                      fontWeight: 400,
                      textTransform: "none",
                      color: LIGHT.textMuted,
                    }}
                  >
                    {t.optional}
                  </span>
                </label>
                <input
                  type="text"
                  value={partnerData.birth_city || ""}
                  onChange={(e) =>
                    updatePartner({ birth_city: e.target.value })
                  }
                  placeholder={t.partnerCityPlaceholder}
                  style={{
                    width: "100%",
                    height: 44,
                    borderRadius: 12,
                    border: `1.5px solid ${LIGHT.cardBorder}`,
                    background: LIGHT.card,
                    color: LIGHT.textDark,
                    padding: "0 12px",
                    fontSize: 14,
                    fontFamily: JP_FONT,
                    outline: "none",
                    boxSizing: "border-box",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                  }}
                />
              </div>
            </div>
          </div>

          {/* Divider */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginBottom: 24,
            }}
          >
            <div style={{ flex: 1, height: 1, background: "#EEF2FF" }} />
            <span
              style={{
                fontSize: 11,
                color: LIGHT.textMuted,
                whiteSpace: "nowrap",
              }}
            >
              ✨
            </span>
            <div style={{ flex: 1, height: 1, background: "#EEF2FF" }} />
          </div>

          {/* Submit Button — disabled until ALL required fields filled */}
          <div style={{ display: "flex", justifyContent: "center" }}>
            <button
              type="button"
              disabled={!bothAllFilled || isLoading}
              onClick={handleCalculate}
              style={{
                minWidth: 280,
                height: 50,
                borderRadius: 9999,
                background:
                  bothAllFilled && !isLoading
                    ? "linear-gradient(135deg, #C084FC 0%, #7DD3FC 100%)"
                    : "#E5E7EB",
                color: bothAllFilled && !isLoading ? "#fff" : "#9CA3AF",
                fontSize: 14,
                fontWeight: 600,
                fontFamily: JP_FONT,
                border: "none",
                cursor: bothAllFilled && !isLoading ? "pointer" : "not-allowed",
                boxShadow:
                  bothAllFilled && !isLoading
                    ? "0 4px 20px rgba(192,132,252,0.35)"
                    : "none",
                transition: "all 0.25s",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                padding: "0 32px",
              }}
            >
              {isLoading ? (
                <>
                  <span
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      border: "2px solid rgba(255,255,255,0.4)",
                      borderTopColor: "#fff",
                      animation: "spin 0.7s linear infinite",
                      display: "inlineBlock",
                    }}
                  />
                  {t.loadingButton}
                </>
              ) : (
                <>{t.submitButton}</>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── MOBILE LAYOUT (responsive white design — same style as desktop, stacked) ──
  return (
    <div
      className="w-full bg-white px-4 sm:px-6 py-5 sm:py-6"
      style={{
        fontFamily: JP_FONT,
        maxWidth: 960,
        margin: "0 auto",
        boxSizing: "border-box",
      }}
    >
      {/* Page title */}
      <div style={{ textAlign: "center", marginBottom: 24 }}>
        <p
          style={{
            fontSize: 20,
            fontWeight: 600,
            color: LIGHT.textLight,
            margin: "0 0 4px",
            fontFamily: JP_FONT,
          }}
        >
          {t.pageTitle}
        </p>
        <p
          style={{
            fontSize: 12,
            color: LIGHT.textMuted,
            margin: 0,
            fontFamily: JP_FONT,
          }}
        >
          {t.pageSubtitle}
        </p>
      </div>

      {/* 2-column form grid — responsive: 2 cols on desktop, 1 col stacked on mobile */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: 20,
          marginBottom: 28,
        }}
      >
        {/* ── Person A (You) ── */}
        <div
          style={{
            background: LIGHT.card,
            borderRadius: 16,
            padding: "18px 16px",
            boxShadow: LIGHT.shadow,
          }}
        >
          <div
            style={{
              marginBottom: 14,
              paddingBottom: 10,
              borderBottom: "2px solid #EEF2FF",
            }}
          >
            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: LIGHT.amethyst,
                fontFamily: JP_FONT,
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: LIGHT.amethyst,
                  display: "inlineBlock",
                  flexShrink: 0,
                }}
              />
              {t.personAHeading}
            </span>
          </div>

          {/* Blood Type */}
          <div style={{ marginBottom: 14 }}>
            <label
              style={{
                display: "block",
                fontSize: 11,
                fontWeight: 600,
                color: LIGHT.textMuted,
                fontFamily: JP_FONT,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                marginBottom: 7,
              }}
            >
              {t.bloodTypeLabel}
            </label>
            <div style={{ display: "flex", gap: 8 }}>
              {BLOOD_TYPES.map((bt) => (
                <button
                  key={bt}
                  type="button"
                  onClick={() => updateSelf({ blood_type: bt })}
                  style={{
                    flex: 1,
                    height: 42,
                    borderRadius: 10,
                    background:
                      selfData.blood_type === bt
                        ? LIGHT.amethystSoft
                        : LIGHT.card,
                    border: `1.5px solid ${selfData.blood_type === bt ? LIGHT.cardBorderActive : LIGHT.cardBorder}`,
                    color:
                      selfData.blood_type === bt
                        ? LIGHT.amethyst
                        : LIGHT.textDark,
                    fontSize: 14,
                    fontWeight: 700,
                    fontFamily: JP_FONT,
                    cursor: "pointer",
                    transition: "all 0.18s",
                    boxShadow:
                      selfData.blood_type === bt
                        ? `0 0 0 3px ${LIGHT.amethystSoft}`
                        : "none",
                  }}
                >
                  {bt}
                </button>
              ))}
            </div>
          </div>

          {/* Date of Birth */}
          <div style={{ marginBottom: 14 }}>
            <label
              style={{
                display: "block",
                fontSize: 11,
                fontWeight: 600,
                color: LIGHT.textMuted,
                fontFamily: JP_FONT,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                marginBottom: 7,
              }}
            >
              {t.dobLabel}
            </label>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "2fr 1fr 1fr",
                gap: 8,
              }}
            >
              <select
                value={selfData.dob ? selfData.dob.split("/")[2] || "" : ""}
                onChange={(e) => {
                  const d = selfData.dob?.split("/") || ["", "", ""];
                  const updated = `${d[0] || ""}/${d[1] || ""}/${e.target.value}`;
                  updateSelf({ dob: e.target.value ? updated : null });
                }}
                style={{
                  height: 42,
                  borderRadius: 10,
                  border: `1.5px solid ${LIGHT.cardBorder}`,
                  background: LIGHT.card,
                  color: LIGHT.textDark,
                  padding: "0 10px",
                  fontSize: 13,
                  fontFamily: JP_FONT,
                  outline: "none",
                  cursor: "pointer",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                }}
              >
                <option value="">{t.yearOption}</option>
                {YEARS.map((y) => (
                  <option key={y.value} value={y.value}>
                    {y.label}
                  </option>
                ))}
              </select>
              <select
                value={selfData.dob ? selfData.dob.split("/")[1] || "" : ""}
                onChange={(e) => {
                  const d = selfData.dob?.split("/") || ["", "", ""];
                  const updated = `${d[0] || ""}/${e.target.value}/${d[2] || ""}`;
                  updateSelf({ dob: e.target.value ? updated : null });
                }}
                style={{
                  height: 42,
                  borderRadius: 10,
                  border: `1.5px solid ${LIGHT.cardBorder}`,
                  background: LIGHT.card,
                  color: LIGHT.textDark,
                  padding: "0 8px",
                  fontSize: 13,
                  fontFamily: JP_FONT,
                  outline: "none",
                  cursor: "pointer",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                }}
              >
                <option value="">{t.monthOptionShort}</option>
                {MONTHS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
              <select
                value={selfData.dob ? selfData.dob.split("/")[0] || "" : ""}
                onChange={(e) => {
                  const d = selfData.dob?.split("/") || ["", "", ""];
                  const updated = `${e.target.value}/${d[1] || ""}/${d[2] || ""}`;
                  updateSelf({ dob: e.target.value ? updated : null });
                }}
                style={{
                  height: 42,
                  borderRadius: 10,
                  border: `1.5px solid ${LIGHT.cardBorder}`,
                  background: LIGHT.card,
                  color: LIGHT.textDark,
                  padding: "0 8px",
                  fontSize: 13,
                  fontFamily: JP_FONT,
                  outline: "none",
                  cursor: "pointer",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                }}
              >
                <option value="">{t.dayOptionShort}</option>
                {DAYS.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Destiny Time */}
          <div style={{ marginBottom: 14 }}>
            <label
              style={{
                display: "block",
                fontSize: 11,
                fontWeight: 600,
                color: LIGHT.textMuted,
                fontFamily: JP_FONT,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                marginBottom: 7,
              }}
            >
              {t.destinyTimeLabel}{" "}
              <span style={{ fontWeight: 400, textTransform: "none" }}>
                {t.optional}
              </span>
            </label>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8,
              }}
            >
              <select
                value={
                  selfData.destiny_time
                    ? selfData.destiny_time.split(":")[0] || ""
                    : ""
                }
                onChange={(e) =>
                  updateSelf({
                    destiny_time: e.target.value
                      ? `${e.target.value}:${selfData.destiny_time?.split(":")[1] || "00"}`
                      : null,
                  })
                }
                style={{
                  height: 42,
                  borderRadius: 10,
                  border: `1.5px solid ${LIGHT.cardBorder}`,
                  background: LIGHT.card,
                  color: LIGHT.textDark,
                  padding: "0 10px",
                  fontSize: 13,
                  fontFamily: JP_FONT,
                  outline: "none",
                  cursor: "pointer",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                }}
              >
                <option value="">{t.hourOptionShort}</option>
                {HOURS.map((h) => (
                  <option key={h.value} value={h.value}>
                    {h.label}
                  </option>
                ))}
              </select>
              <select
                value={
                  selfData.destiny_time
                    ? selfData.destiny_time.split(":")[1] || ""
                    : ""
                }
                onChange={(e) =>
                  updateSelf({
                    destiny_time: selfData.destiny_time
                      ? `${selfData.destiny_time.split(":")[0]}:${e.target.value}`
                      : null,
                  })
                }
                style={{
                  height: 42,
                  borderRadius: 10,
                  border: `1.5px solid ${LIGHT.cardBorder}`,
                  background: LIGHT.card,
                  color: LIGHT.textDark,
                  padding: "0 10px",
                  fontSize: 13,
                  fontFamily: JP_FONT,
                  outline: "none",
                  cursor: "pointer",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                }}
              >
                <option value="">{t.minuteOptionShort}</option>
                {MINUTES_5.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Birth City */}
          <div>
            <label
              style={{
                display: "block",
                fontSize: 11,
                fontWeight: 600,
                color: LIGHT.textMuted,
                fontFamily: JP_FONT,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                marginBottom: 7,
              }}
            >
              {t.placeOfBirthLabel}{" "}
              <span style={{ fontWeight: 400, textTransform: "none" }}>
                {t.optional}
              </span>
            </label>
            <input
              type="text"
              value={selfData.birth_city || ""}
              onChange={(e) => updateSelf({ birth_city: e.target.value })}
              placeholder={t.selfCityPlaceholder}
              style={{
                width: "100%",
                height: 42,
                borderRadius: 10,
                border: `1.5px solid ${LIGHT.cardBorder}`,
                background: LIGHT.card,
                color: LIGHT.textDark,
                padding: "0 12px",
                fontSize: 14,
                fontFamily: JP_FONT,
                outline: "none",
                boxSizing: "border-box",
                boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
              }}
            />
          </div>
        </div>

        {/* ── Person B (Partner) ── */}
        <div
          style={{
            background: LIGHT.card,
            borderRadius: 16,
            padding: "18px 16px",
            boxShadow: LIGHT.shadow,
          }}
        >
          <div
            style={{
              marginBottom: 14,
              paddingBottom: 10,
              borderBottom: "2px solid #EEF2FF",
            }}
          >
            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "#7DD3FC",
                fontFamily: JP_FONT,
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: "#7DD3FC",
                  display: "inlineBlock",
                  flexShrink: 0,
                }}
              />
              {t.personBHeading}
            </span>
          </div>

          {/* Blood Type */}
          <div style={{ marginBottom: 14 }}>
            <label
              style={{
                display: "block",
                fontSize: 11,
                fontWeight: 600,
                color: LIGHT.textMuted,
                fontFamily: JP_FONT,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                marginBottom: 7,
              }}
            >
              {t.bloodTypeLabel}
            </label>
            <div style={{ display: "flex", gap: 8 }}>
              {BLOOD_TYPES.map((bt) => (
                <button
                  key={bt}
                  type="button"
                  onClick={() => updatePartner({ blood_type: bt })}
                  style={{
                    flex: 1,
                    height: 42,
                    borderRadius: 10,
                    background:
                      partnerData.blood_type === bt
                        ? "rgba(125,211,252,0.15)"
                        : LIGHT.card,
                    border: `1.5px solid ${partnerData.blood_type === bt ? "#7DD3FC" : LIGHT.cardBorder}`,
                    color:
                      partnerData.blood_type === bt
                        ? "#7DD3FC"
                        : LIGHT.textDark,
                    fontSize: 14,
                    fontWeight: 700,
                    fontFamily: JP_FONT,
                    cursor: "pointer",
                    transition: "all 0.18s",
                    boxShadow:
                      partnerData.blood_type === bt
                        ? "0 0 0 3px rgba(125,211,252,0.15)"
                        : "none",
                  }}
                >
                  {bt}
                </button>
              ))}
            </div>
          </div>

          {/* Date of Birth */}
          <div style={{ marginBottom: 14 }}>
            <label
              style={{
                display: "block",
                fontSize: 11,
                fontWeight: 600,
                color: LIGHT.textMuted,
                fontFamily: JP_FONT,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                marginBottom: 7,
              }}
            >
              {t.dobLabel}
            </label>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "2fr 1fr 1fr",
                gap: 8,
              }}
            >
              <select
                value={
                  partnerData.dob ? partnerData.dob.split("/")[2] || "" : ""
                }
                onChange={(e) => {
                  const d = partnerData.dob?.split("/") || ["", "", ""];
                  const updated = `${d[0] || ""}/${d[1] || ""}/${e.target.value}`;
                  updatePartner({ dob: e.target.value ? updated : null });
                }}
                style={{
                  height: 42,
                  borderRadius: 10,
                  border: `1.5px solid ${LIGHT.cardBorder}`,
                  background: LIGHT.card,
                  color: LIGHT.textDark,
                  padding: "0 10px",
                  fontSize: 13,
                  fontFamily: JP_FONT,
                  outline: "none",
                  cursor: "pointer",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                }}
              >
                <option value="">{t.yearOption}</option>
                {YEARS.map((y) => (
                  <option key={y.value} value={y.value}>
                    {y.label}
                  </option>
                ))}
              </select>
              <select
                value={
                  partnerData.dob ? partnerData.dob.split("/")[1] || "" : ""
                }
                onChange={(e) => {
                  const d = partnerData.dob?.split("/") || ["", "", ""];
                  const updated = `${d[0] || ""}/${e.target.value}/${d[2] || ""}`;
                  updatePartner({ dob: e.target.value ? updated : null });
                }}
                style={{
                  height: 42,
                  borderRadius: 10,
                  border: `1.5px solid ${LIGHT.cardBorder}`,
                  background: LIGHT.card,
                  color: LIGHT.textDark,
                  padding: "0 8px",
                  fontSize: 13,
                  fontFamily: JP_FONT,
                  outline: "none",
                  cursor: "pointer",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                }}
              >
                <option value="">{t.monthOptionShort}</option>
                {MONTHS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
              <select
                value={
                  partnerData.dob ? partnerData.dob.split("/")[0] || "" : ""
                }
                onChange={(e) => {
                  const d = partnerData.dob?.split("/") || ["", "", ""];
                  const updated = `${e.target.value}/${d[1] || ""}/${d[2] || ""}`;
                  updatePartner({ dob: e.target.value ? updated : null });
                }}
                style={{
                  height: 42,
                  borderRadius: 10,
                  border: `1.5px solid ${LIGHT.cardBorder}`,
                  background: LIGHT.card,
                  color: LIGHT.textDark,
                  padding: "0 8px",
                  fontSize: 13,
                  fontFamily: JP_FONT,
                  outline: "none",
                  cursor: "pointer",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                }}
              >
                <option value="">{t.dayOptionShort}</option>
                {DAYS.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Destiny Time */}
          <div style={{ marginBottom: 14 }}>
            <label
              style={{
                display: "block",
                fontSize: 11,
                fontWeight: 600,
                color: LIGHT.textMuted,
                fontFamily: JP_FONT,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                marginBottom: 7,
              }}
            >
              {t.destinyTimeLabel}{" "}
              <span style={{ fontWeight: 400, textTransform: "none" }}>
                {t.optional}
              </span>
            </label>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8,
              }}
            >
              <select
                value={
                  partnerData.destiny_time
                    ? partnerData.destiny_time.split(":")[0] || ""
                    : ""
                }
                onChange={(e) =>
                  updatePartner({
                    destiny_time: e.target.value
                      ? `${e.target.value}:${partnerData.destiny_time?.split(":")[1] || "00"}`
                      : null,
                  })
                }
                style={{
                  height: 42,
                  borderRadius: 10,
                  border: `1.5px solid ${LIGHT.cardBorder}`,
                  background: LIGHT.card,
                  color: LIGHT.textDark,
                  padding: "0 10px",
                  fontSize: 13,
                  fontFamily: JP_FONT,
                  outline: "none",
                  cursor: "pointer",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                }}
              >
                <option value="">{t.hourOptionShort}</option>
                {HOURS.map((h) => (
                  <option key={h.value} value={h.value}>
                    {h.label}
                  </option>
                ))}
              </select>
              <select
                value={
                  partnerData.destiny_time
                    ? partnerData.destiny_time.split(":")[1] || ""
                    : ""
                }
                onChange={(e) =>
                  updatePartner({
                    destiny_time: partnerData.destiny_time
                      ? `${partnerData.destiny_time.split(":")[0]}:${e.target.value}`
                      : null,
                  })
                }
                style={{
                  height: 42,
                  borderRadius: 10,
                  border: `1.5px solid ${LIGHT.cardBorder}`,
                  background: LIGHT.card,
                  color: LIGHT.textDark,
                  padding: "0 10px",
                  fontSize: 13,
                  fontFamily: JP_FONT,
                  outline: "none",
                  cursor: "pointer",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                }}
              >
                <option value="">{t.minuteOptionShort}</option>
                {MINUTES_5.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Birth City */}
          <div>
            <label
              style={{
                display: "block",
                fontSize: 11,
                fontWeight: 600,
                color: LIGHT.textMuted,
                fontFamily: JP_FONT,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                marginBottom: 7,
              }}
            >
              {t.placeOfBirthLabel}{" "}
              <span style={{ fontWeight: 400, textTransform: "none" }}>
                {t.optional}
              </span>
            </label>
            <input
              type="text"
              value={partnerData.birth_city || ""}
              onChange={(e) => updatePartner({ birth_city: e.target.value })}
              placeholder={t.partnerCityPlaceholder}
              style={{
                width: "100%",
                height: 42,
                borderRadius: 10,
                border: `1.5px solid ${LIGHT.cardBorder}`,
                background: LIGHT.card,
                color: LIGHT.textDark,
                padding: "0 12px",
                fontSize: 14,
                fontFamily: JP_FONT,
                outline: "none",
                boxSizing: "border-box",
                boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
              }}
            />
          </div>
        </div>
      </div>

      {/* Divider */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 20,
        }}
      >
        <div style={{ flex: 1, height: 1, background: "#EEF2FF" }} />
        <span style={{ fontSize: 11, color: LIGHT.textMuted }}>✨</span>
        <div style={{ flex: 1, height: 1, background: "#EEF2FF" }} />
      </div>

      {/* Submit Button */}
      <div style={{ display: "flex", justifyContent: "center" }}>
        <button
          type="button"
          disabled={!bothAllFilled || isLoading}
          onClick={handleCalculate}
          style={{
            minWidth: 260,
            height: 48,
            borderRadius: 9999,
            background:
              bothAllFilled && !isLoading
                ? "linear-gradient(135deg, #C084FC 0%, #7DD3FC 100%)"
                : "#E5E7EB",
            color: bothAllFilled && !isLoading ? "#fff" : "#9CA3AF",
            fontSize: 14,
            fontWeight: 600,
            fontFamily: JP_FONT,
            border: "none",
            cursor: bothAllFilled && !isLoading ? "pointer" : "not-allowed",
            boxShadow:
              bothAllFilled && !isLoading
                ? "0 4px 20px rgba(192,132,252,0.35)"
                : "none",
            transition: "all 0.25s",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            padding: "0 28px",
          }}
        >
          {isLoading ? (
            <>
              <span
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  border: "2px solid rgba(255,255,255,0.4)",
                  borderTopColor: "#fff",
                  animation: "spin 0.7s linear infinite",
                  display: "inlineBlock",
                }}
              />
              {t.loadingButton}
            </>
          ) : (
            <>{t.submitButton}</>
          )}
        </button>
      </div>
    </div>
  );
};

export default JapanCompatibility3Box;
export type { Japan3BoxData };
